import { useMonthPeriodStore } from '../../store/useMonthPeriodStore'
import { Spinner } from '@heroui/react'
import { StationPanelLinePhoto } from '../panels/StationPanelLinePhoto'
import { MonthPeriodPicker } from './shared/MonthPeriodPicker'
import { MinGrid } from './shared/MinGrid'
import { TableOrientationToggle } from './shared/TableOrientationToggle'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'
import { useDlyOneMonthData } from '../../hooks/useDlyOneMonthData'
import { cldWindVal2TimeDLY } from './shared/weatherUtils'

const SLUG = 'dly-prehled'

export const DLY_COLUMNS = [
  {
    group: 'Teploty',
    subgroups: null,
    cols: [
      { tsId: 'DLY_TEMPAVG',    label: 'Prům',      unit: '°C' },
      { tsId: 'DLY_TNMIN',      label: 'Min.',       unit: '°C' },
      { tsId: 'DLY_TNNIGHTMIN', label: 'Min. noc',   unit: '°C' },
      { tsId: 'DLY_TXMAX',      label: 'Max.',        unit: '°C' },
      { tsId: 'DLY_TGROUNDMIN', label: 'Přízemní',   unit: '°C' },
    ],
  },
  {
    group: 'Vítr',
    subgroups: [
      {
        label: 'Prům.',
        cols: [
          { tsId: 'DLY_WSAVG', label: 'Prům',      unit: 'm/s' },
        ],
      },
      {
        label: 'Max.',
        cols: [
          { tsId: 'DLY_WSMAX', label: 'Max.',       unit: 'm/s' },
          { tsId: 'DLY_WDMAX', label: 'Směr max.',  unit: '°' },
          { tsId: 'DLY_WTMAX', label: 'Čas max.',   unit: 'čas SEČ', formatCell: (v) => cldWindVal2TimeDLY(v) },
        ],
      },
    ],
    cols: null,
  },
  {
    group: 'Tlak',
    subgroups: null,
    cols: [
      { tsId: 'DLY_PRESSAVG',    label: 'Stan.',      unit: 'hPa' },
      { tsId: 'DLY_VAPPRESSAVG', label: 'Vod. páry',  unit: 'hPa' },
    ],
  },
  {
    group: 'Sluneční',
    subgroups: [{
        label: 'Svit',
        cols: [
      { tsId: 'DLY_SUNSHINESUM', label: 'Svit', unit: 'hod.' },
    ],
      },],
    
  },
  {
    group: 'Srážky',
    subgroups: null,
    cols: [
      { tsId: 'DLY_PRECIPSUM', label: 'Srážky',      unit: 'mm' },
      { tsId: 'DLY_API30',  label: 'Indx. před.', unit: 'mm' },
    ],
  },
  {
    group: 'Sníh',
    subgroups: null,
    cols: [
      { tsId: 'DLY_SNOW',    label: 'Celkem', unit: 'cm' },
      { tsId: 'DLY_SNOWNEW', label: 'Nový',   unit: 'cm' },
    ],
  },
  {
    group: 'Vlhkost',
    subgroups: null,
    cols: [
      { tsId: 'DLY_RAWHUMIDITYAVG', label: 'Prům', unit: '%' },
    ],
  },
  {
    group: 'Půdní teploty',
    subgroups: null,
    cols: [
      { tsId: 'DLY_SOIL05AVG',  label: '5 cm',   unit: '°C' },
      { tsId: 'DLY_SOIL10AVG',  label: '10 cm',  unit: '°C' },
      { tsId: 'DLY_SOIL20AVG',  label: '20 cm',  unit: '°C' },
      { tsId: 'DLY_SOIL50AVG',  label: '50 cm',  unit: '°C' },
      { tsId: 'DLY_SOIL100AVG', label: '100 cm', unit: '°C' },
    ],
  },
]

// Transponovaná hlavička (řádky = veličiny). Skupina (row.group) → oddělovací řádek hlavičky.
export const DLY_HEADER_TRANSPOSED = {
  groupLabel: 'Veličina',
  rows: [
    { tsId: 'DLY_TEMPAVG',      group: 'Teploty',       label: 'Průměrná teplota',          unit: '°C' },
    { tsId: 'DLY_TNMIN',        group: 'Teploty',       label: 'Minimální teplota',         unit: '°C' },
    { tsId: 'DLY_TNNIGHTMIN',   group: 'Teploty',       label: 'Minimální noční teplota',   unit: '°C' },
    { tsId: 'DLY_TXMAX',        group: 'Teploty',       label: 'Maximální teplota',         unit: '°C' },
    { tsId: 'DLY_TGROUNDMIN',   group: 'Teploty',       label: 'Minimální přízemní teplota', unit: '°C' },
    { tsId: 'DLY_WSAVG',        group: 'Vítr',          label: 'Průměrná rychlost větru',   unit: 'm/s' },
    { tsId: 'DLY_WSMAX',        group: 'Vítr',          label: 'Maximální rychlost větru',  unit: 'm/s' },
    { tsId: 'DLY_WDMAX',        group: 'Vítr',          label: 'Směr maximálního větru',    unit: '°' },
    { tsId: 'DLY_WTMAX',        group: 'Vítr',          label: 'Čas maxima větru',          unit: 'čas SEČ', formatCell: (v) => cldWindVal2TimeDLY(v) },
    { tsId: 'DLY_PRESSAVG',     group: 'Tlak',          label: 'Průměrný tlak na stanici',  unit: 'hPa' },
    { tsId: 'DLY_VAPPRESSAVG',  group: 'Tlak',          label: 'Průměrný tlak vodních par', unit: 'hPa' },
    { tsId: 'DLY_SUNSHINESUM',  group: 'Sluneční',      label: 'Sluneční svit',             unit: 'hod.' },
    { tsId: 'DLY_PRECIPSUM',    group: 'Srážky',        label: 'Úhrn srážek',               unit: 'mm' },
    { tsId: 'DLY_API30',        group: 'Srážky',        label: 'Index předchozích srážek',  unit: 'mm' },
    { tsId: 'DLY_SNOW',         group: 'Sníh',          label: 'Celková výška sněhu',       unit: 'cm' },
    { tsId: 'DLY_SNOWNEW',      group: 'Sníh',          label: 'Nový sníh',                 unit: 'cm' },
    { tsId: 'DLY_RAWHUMIDITYAVG', group: 'Vlhkost',     label: 'Průměrná relativní vlhkost', unit: '%' },
    { tsId: 'DLY_SOIL05AVG',    group: 'Půdní teploty', label: 'Půdní teplota 5 cm',        unit: '°C' },
    { tsId: 'DLY_SOIL10AVG',    group: 'Půdní teploty', label: 'Půdní teplota 10 cm',       unit: '°C' },
    { tsId: 'DLY_SOIL20AVG',    group: 'Půdní teploty', label: 'Půdní teplota 20 cm',       unit: '°C' },
    { tsId: 'DLY_SOIL50AVG',    group: 'Půdní teploty', label: 'Půdní teplota 50 cm',       unit: '°C' },
    { tsId: 'DLY_SOIL100AVG',   group: 'Půdní teploty', label: 'Půdní teplota 100 cm',      unit: '°C' },
  ],
}

export function DayGrid({ columns, dataMap, labelMap, orientation, reverse }) {
  return (
    <MinGrid
      columns={columns}
      headerTransposed={DLY_HEADER_TRANSPOSED}
      orientation={orientation}
      dataMap={dataMap}
      labelMap={labelMap}
      rowLabel="Den"
      reverse={reverse}
      rowGroupBorder={(key) => {
        const day = parseInt(key.slice(8, 10), 10)
        return [6, 11, 16, 21, 26].includes(day)
      }}
      rowGroupEndBorder={(key) => {
        const day = parseInt(key.slice(8, 10), 10)
        return [5, 10, 15, 20, 25].includes(day)
      }}
    />
  )
}


export function DlyOneMonthView({ station }) {
  const { year, month, setYear, setMonth } = useMonthPeriodStore()
  const orientation = useTableOrientationStore((s) => s.getOrientation(SLUG))

  const { dataMap, labelMap, isLoading, isError } = useDlyOneMonthData(station?.gh_id, year, month, DLY_COLUMNS)

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        {station && <StationPanelLinePhoto station={station} />}
        <div className="flex items-center justify-between gap-3">
          <MonthPeriodPicker
            year={year}
            month={month}
            onYearChange={setYear}
            onMonthChange={setMonth}
            obsType="DLY"
          />
          <TableOrientationToggle slug={SLUG} />
        </div>
      </div>

      {/* Grid area */}
      <div className="flex-1 min-h-0 p-4 flex flex-col">
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Spinner size="sm" /> Načítání dat…
          </div>
        )}
        {isError && (
          <p className="text-red-500 text-sm">Chyba při načítání dat</p>
        )}
        {!isLoading && !isError && (
          <DayGrid columns={DLY_COLUMNS} dataMap={dataMap} labelMap={labelMap} orientation={orientation} />
        )}
      </div>
    </div>
  )
}
